import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Text } from "@andrum/ui";
import {
  submitFeedback,
  type FeedbackSource,
} from "@/features/learn/api/feedback-api";

type Props = {
  source: FeedbackSource;
  sourceId?: string | null;
  className?: string;
};

export function FeedbackBar({ source, sourceId, className }: Props) {
  const { t } = useTranslation();
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [showNote, setShowNote] = useState(false);

  const send = async (rating: number) => {
    if (busy || sent) return;
    setBusy(true);
    const result = await submitFeedback({
      source,
      sourceId: sourceId ?? null,
      rating,
      body: note.trim() || null,
    });
    setBusy(false);
    if (result.ok) setSent(true);
  };

  if (sent) {
    return (
      <Text variant="caption" className={className}>
        {t("feedback.thanks")}
      </Text>
    );
  }

  return (
    <div className={className ?? "space-y-2"}>
      <Text variant="caption" className="text-foreground-muted">
        {t("feedback.wasUseful")}
      </Text>
      <div className="flex gap-2">
        <Button
          type="button"
          variant="soft"
          className="min-h-10 flex-1"
          disabled={busy}
          onClick={() => void send(1)}
        >
          {t("feedback.yes")}
        </Button>
        <Button
          type="button"
          variant="ghost"
          className="min-h-10 flex-1"
          disabled={busy}
          onClick={() => void send(0)}
        >
          {t("feedback.ok")}
        </Button>
        <Button
          type="button"
          variant="ghost"
          className="min-h-10 flex-1"
          disabled={busy}
          onClick={() => void send(-1)}
        >
          {t("feedback.no")}
        </Button>
      </div>
      {!showNote ? (
        <button
          type="button"
          className="text-xs text-primary"
          onClick={() => setShowNote(true)}
        >
          {t("feedback.addNote")}
        </button>
      ) : (
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value.slice(0, 500))}
          rows={2}
          placeholder={t("feedback.notePlaceholder")}
          className="w-full rounded-xl border border-border bg-surface-raised px-3 py-2 text-sm"
        />
      )}
    </div>
  );
}
